import { DataContextProvider } from "contexts/DataContext";
import { useLocalizedData } from "contexts/LocalizationContext/hooks";
import { getTranslations } from "contexts/LocalizationContext/utils";
import useStory from "hooks/useStory";
import { AHPLUS_PRICE } from "modules/storyblok/components/AhPlusPricingPlans/config";
import type { TemplateContent } from "modules/storyblok/templates/AirhelpPlus/AirhelpPlus.d";
import type { BaseTemplate } from "modules/storyblok/types";
import type { GetServerSideProps } from "next";
import dynamic from "next/dynamic";
import { useEffect } from "react";
import { checkIfValidLanguage } from "utils/localeUtils";
import replaceAll from "utils/replaceAll";
import getDataFromCMS from "utils/serverSideUtils";
import { getAntiFlickerSettings, getWhiteListDomains } from "utils/serverSideUtils/datasources";
import getServerSideStoryblokPages from "utils/serverSideUtils/getServerSideStoryblokPages";
import { isPageTrackingless } from "utils/trackingUtils";

// TODO | Replace any after migration Templates to TS

const MagnitudeTemplate = dynamic<any>(() => import("modules/magnitude/templates").then((m) => m.MagnitudeTemplate));
const MagnitudeTopicTemplate = dynamic<any>(() =>
  import("modules/magnitude/templates").then((m) => m.MagnitudeTopicTemplate)
);
const BasicTemplate = dynamic<any>(() => import("modules/storyblok/templates").then((m) => m.BasicTemplate));
const BlogPostTemplate = dynamic<any>(() => import("modules/storyblok/templates").then((m) => m.BlogPostTemplate));
const BlogHomeTemplate = dynamic<any>(() => import("modules/storyblok/templates").then((m) => m.BlogHomeTemplate));
const PressHomeTemplate = dynamic<any>(() => import("modules/storyblok/templates").then((m) => m.PressHomeTemplate));
const FlexibleTemplate = dynamic<any>(() => import("modules/storyblok/templates").then((m) => m.FlexibleTemplate));
const ReferralTemplate = dynamic<any>(() => import("modules/storyblok/templates").then((m) => m.ReferralTemplate));
const SideBarTemplate = dynamic<any>(() => import("modules/storyblok/templates").then((m) => m.SideBarTemplate));
const IframeTemplate = dynamic<any>(() => import("modules/storyblok/templates").then((m) => m.IframeTemplate));
const HelpHomeTemplate = dynamic<any>(() => import("modules/storyblok/templates").then((m) => m.HelpHomeTemplate));
const AirlineRanking = dynamic<any>(() => import("modules/storyblok/templates").then((m) => m.AirlineRanking));
const AirportRanking = dynamic<any>(() => import("modules/storyblok/templates").then((m) => m.AirportRanking));
const AirHelpPlus = dynamic<BaseTemplate<TemplateContent>>(() =>
  import("modules/storyblok/templates").then((m) => m.AirHelpPlus)
);

// TODO | Add types after migration _app.js to TS

const Template = ({ templateComponent, ...props }: any) => {
  const storyContent = useStory(props.story);
  const bridgedContentProps = props;

  useEffect(() => {
    bridgedContentProps.story = storyContent;
  }, [storyContent, bridgedContentProps]);

  switch (templateComponent) {
    case "magnitude": {
      return <MagnitudeTemplate {...props} />;
    }
    case "magnitudeTopic": {
      return <MagnitudeTopicTemplate {...props} />;
    }
    case "basicTemplate": {
      return <BasicTemplate {...bridgedContentProps} />;
    }
    case "blogPost": {
      return <BlogPostTemplate {...bridgedContentProps} />;
    }
    case "blogHome": {
      return <BlogHomeTemplate {...props} />;
    }
    case "pressHome": {
      return <PressHomeTemplate {...props} />;
    }
    case "flexibleTemplate": {
      return <FlexibleTemplate {...bridgedContentProps} />;
    }
    case "referralTemplate": {
      return <ReferralTemplate {...props} />;
    }
    case "sideBarTemplate": {
      return <SideBarTemplate {...bridgedContentProps} />;
    }
    case "iframeTemplate": {
      return <IframeTemplate {...props} />;
    }
    case "helpHome": {
      return <HelpHomeTemplate {...props} />;
    }
    case "airlineRanking": {
      return <AirlineRanking {...props} />;
    }
    case "airportRanking": {
      return <AirportRanking {...props} />;
    }
    case "airHelpPlus": {
      return <AirHelpPlus {...props} />;
    }
    default:
      throw new Error(`Unexpected Template: ${templateComponent}`);
  }
};

// TODO | Add types after migration _app.js to TS

const Page = (props: any) => {
  const { getCompensationValue, getLocalizedCurrency } = useLocalizedData();
  const { serverSideStoryblokPages, story } = props;

  let content = props;

  if (story) {
    const replacers = {
      "{{commission_value}}": getCompensationValue(),
      "{{compensation_value}}": getCompensationValue(),
      "{{luggage_compensation_value}}": getCompensationValue("luggage"),
      "{{extra_expenses_compensation_value}}": getCompensationValue("extraExpenses"),
      "{{ah_price_value}}": getLocalizedCurrency(AHPLUS_PRICE, "EUR", 2),
      "{{ec261_compensation}}": getCompensationValue("ec261Compensation"),
      "{{brazil_compensation}}": getCompensationValue("brazilCompensation"),
      "{{uk261_compensation}}": getCompensationValue("uk261Compensation"),
      "{{passengers_compensated}}": "1.3",
    };

    const updatedContent = replaceAll(story, replacers);

    content = { ...props, story: updatedContent };
  }

  return (
    <DataContextProvider data={{ serverSideStoryblokPages }}>
      <Template {...content} />
    </DataContextProvider>
  );
};

export default Page;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context;
  const { language } = query;

  if (!checkIfValidLanguage(language)) {
    return {
      notFound: true,
    };
  }

  // TODO | Add types after migration getTranslations, getDataFromCMS, etc to TS
  const [translations, cmsResponse, antiFlickerSettings, serverSideStoryblokPages, whiteListDomains] =
    await Promise.all<any>([
      getTranslations(language, []),
      getDataFromCMS(context),
      getAntiFlickerSettings(),
      getServerSideStoryblokPages(),
      getWhiteListDomains(),
    ]);

  if (cmsResponse.errorCode === 404) {
    return {
      notFound: true,
    };
  }

  // Default values for props from SB. We use them on pages which were create before we add some of that new configurations because currently they have values 'undefined'
  // TODO After complete migration to SB we need to check every page which uses these props and assign new values then we can remove default values

  const settings = {
    hideFlagBox: cmsResponse.story?.content?.languageSwitcher || false,
    showStickyBottomBar: cmsResponse.story?.content?.stickyBottomBar || false,
    activeButtonOption: cmsResponse.story?.content?.activeButtonOption || "check-compensation",
  };

  const isTrackingless = isPageTrackingless(query);

  return {
    props: {
      isTrackingless,
      translations,
      language,
      antiFlickerSettings,
      serverSideStoryblokPages,
      settings,
      whiteListDomains,
      ...cmsResponse,
    },
  };
};
